import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { collection, doc, getDocs, runTransaction, serverTimestamp, setDoc, writeBatch } from "firebase/firestore";
import { auth, db } from "../firebase";
import Sidebar from "../components/Sidebar";
import IILSubnav from "../components/IILSubnav";

const MAPPING_COLLECTION = "iil_employee_mappings";
const RUN_COLLECTION = "iil_attendance_runs";
const EMPTY_EMPLOYEE = { clientEmpId: "", employeeCode: "", name: "", salary: "", segment: "", source: "MANUAL", active: true, bank: "", account: "" };
const IIL_SEGMENTS = [
  "Pacakging of PPRC",
  "Admin Service Factory 3",
  "Lahore Office Admin Staff",
  "Maintenance Staff",
  "ISL-Office Boy",
  "iil-Trading ISL",
  "iil-Trading LHR",
  "Multan Office"
];

function text(value) {
  return String(value ?? "").trim();
}

function header(value) {
  return text(value).toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function empId(value) {
  const valueText = text(value);
  if (!valueText) return "";
  if (/^\d+$/.test(valueText)) return valueText.padStart(4, "0");
  return valueText.toUpperCase();
}

function employeeCode(value) {
  const valueText = text(value);
  if (!valueText) return "";
  const number = Number(valueText);
  return Number.isFinite(number) ? String(Math.trunc(number)) : valueText.toUpperCase();
}

function number(value, fallback = 0) {
  const parsed = Number(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : fallback;
}

function findColumn(headers, options) {
  return headers.findIndex((value) => options.some((option) => value === option || value.includes(option)));
}

function findEmployeeHeader(rows) {
  return rows.findIndex((row) => {
    const headers = row.map(header);
    const hasIdentifier = findColumn(headers, ["emp id", "employee id"]) >= 0
      || findColumn(headers, ["employee code", "emp code", "payroll code"]) >= 0;
    return hasIdentifier && findColumn(headers, ["employee name", "name of employee", "name"]) >= 0;
  });
}

function parseWorkbook(fileBuffer) {
  const workbook = XLSX.read(fileBuffer, { cellDates: true });
  const candidates = [];

  workbook.SheetNames.forEach((sheetName) => {
    const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1, defval: "" });
    const headerIndex = findEmployeeHeader(rows);
    if (headerIndex < 0) return;
    const headers = rows[headerIndex].map(header);
    const empIdIndex = findColumn(headers, ["emp id", "employee id"]);
    const nameIndex = findColumn(headers, ["employee name", "name of employee", "name"]);
    const detectedCodeIndex = findColumn(headers, ["employee code", "emp code", "payroll code"]);
    const codeIndex = detectedCodeIndex >= 0
      ? detectedCodeIndex
      : (empIdIndex >= 0 && nameIndex - empIdIndex === 2 ? empIdIndex + 1 : -1);
    const salaryDaysIndex = findColumn(headers, ["salary days", "salary day"]);
    const pIndex = headers.findIndex((value) => value === "p");
    const aIndex = headers.findIndex((value) => value === "a");
    const sIndex = headers.findIndex((value) => value === "s");
    const gIndex = headers.findIndex((value) => value === "g");
    const dayColumns = headers
      .map((value, index) => ({ value, index }))
      .filter(({ value, index }) => index > nameIndex && (/^(mon|tue|wed|thu|fri|sat|sun)/.test(value) || /^\d{1,2}$/.test(value)))
      .map(({ index }) => index);

    const invalidRows = [];
    const parsed = rows.slice(headerIndex + 1).map((row, rowOffset) => {
      const id = empIdIndex >= 0 ? empId(row[empIdIndex]) : "";
      const code = codeIndex >= 0 ? employeeCode(row[codeIndex]) : "";
      const name = text(row[nameIndex]);
      const hasEmployeeData = Boolean(id || code || name);
      if (!hasEmployeeData) return null;
      if ((!id && !code) || !name) {
        invalidRows.push(headerIndex + rowOffset + 2);
        return null;
      }
      const marks = dayColumns.map((index) => text(row[index]).toUpperCase());
      const present = marks.filter((mark) => mark === "P").length;
      const explicitDays = salaryDaysIndex >= 0 ? number(row[salaryDaysIndex], NaN) : NaN;
      return {
        clientEmpId: id,
        employeeCode: code,
        name,
        daysWorked: Number.isFinite(explicitDays) ? explicitDays : present,
        p: pIndex >= 0 ? number(row[pIndex], present) : present,
        a: aIndex >= 0 ? number(row[aIndex], 0) : marks.filter((mark) => mark === "A").length,
        s: sIndex >= 0 ? number(row[sIndex], 0) : marks.filter((mark) => mark === "S").length,
        g: gIndex >= 0 ? number(row[gIndex], 0) : marks.filter((mark) => mark === "G").length,
        marks,
        sheetName
      };
    }).filter(Boolean);
    if (parsed.length || invalidRows.length) candidates.push({ rows: parsed, invalidRows, sheetName, dayCount: dayColumns.length });
  });

  candidates.sort((a, b) => b.rows.length - a.rows.length);
  if (!candidates.length) throw new Error("No sheet containing Emp ID and Employee Name was detected.");
  const selected = candidates[0];
  if (selected.invalidRows.length) {
    throw new Error(`Attendance import stopped: incomplete employee data found on Excel row(s) ${selected.invalidRows.join(", ")}. No employee row may be silently skipped.`);
  }
  const overtimeByEmpId = new Map();
  const overtimeByCode = new Map();
  workbook.SheetNames.filter((name) => header(name).includes("overtime")).forEach((sheetName) => {
    const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1, defval: "" });
    const headerIndex = findEmployeeHeader(rows);
    if (headerIndex < 0) return;
    const headers = rows[headerIndex].map(header);
    const idIndex = findColumn(headers, ["emp id", "employee id"]);
    const codeIndex = findColumn(headers, ["employee code", "emp code"]);
    const totalIndex = findColumn(headers, ["total overtime", "total ot"]);
    if (totalIndex < 0) return;
    rows.slice(headerIndex + 1).forEach((row) => {
      const hours = number(row[totalIndex], 0);
      const id = idIndex >= 0 ? empId(row[idIndex]) : "";
      const code = codeIndex >= 0 ? employeeCode(row[codeIndex]) : "";
      if (id) overtimeByEmpId.set(id, hours);
      if (code) overtimeByCode.set(code, hours);
    });
  });
  selected.rows = selected.rows.map((row) => ({
    ...row,
    employeeCode: row.employeeCode === "10098" ? "10101" : row.employeeCode,
    otHours: overtimeByEmpId.get(row.clientEmpId) ?? overtimeByCode.get(row.employeeCode) ?? 0
  }));
  return selected;
}

function parseProfileWorkbook(fileBuffer) {
  const workbook = XLSX.read(fileBuffer, { cellDates: true });
  const employees = [];
  workbook.SheetNames.forEach((sheetName) => {
    const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1, defval: "" });
    const headerIndex = rows.findIndex((row) => {
      const headers = row.map(header);
      return findColumn(headers, ["employee code"]) >= 0 && findColumn(headers, ["salary"]) >= 0 && findColumn(headers, ["segment"]) >= 0;
    });
    if (headerIndex < 0) return;
    const headers = rows[headerIndex].map(header);
    const index = (names) => findColumn(headers, names);
    const codeIndex = index(["employee code"]);
    const nameIndex = index(["name of employee", "employee name", "name"]);
    const salaryIndex = index(["salary"]);
    const segmentIndex = index(["segment"]);
    const cardIndex = index(["employee card no", "card no"]);
    const fatherIndex = index(["father name"]);
    const cnicIndex = index(["cnic"]);
    const departmentIndex = index(["department"]);
    const designationIndex = index(["designation"]);
    const dojIndex = index(["date of joining", "doj"]);
    const bankIndex = index(["bank"]);
    const accountIndex = index(["account number", "account"]);
    const modeIndex = index(["pmt mode", "payment mode"]);
    rows.slice(headerIndex + 1).forEach((row) => {
      const code = employeeCode(row[codeIndex]);
      const name = text(row[nameIndex]);
      const salary = number(row[salaryIndex], NaN);
      if (!code || !name || !Number.isFinite(salary)) return;
      employees.push({
        employeeCode: code,
        clientEmpId: "",
        name,
        salary,
        segment: text(row[segmentIndex]),
        cardNo: cardIndex >= 0 ? text(row[cardIndex]) : "",
        fatherName: fatherIndex >= 0 ? text(row[fatherIndex]) : "",
        cnic: cnicIndex >= 0 ? text(row[cnicIndex]) : "",
        department: departmentIndex >= 0 ? text(row[departmentIndex]) : "",
        designation: designationIndex >= 0 ? text(row[designationIndex]) : "",
        doj: dojIndex >= 0 ? text(row[dojIndex]) : "",
        bank: bankIndex >= 0 ? text(row[bankIndex]) : "",
        account: accountIndex >= 0 ? text(row[accountIndex]) : "",
        pmtMode: modeIndex >= 0 ? text(row[modeIndex]) : "",
        source: "MANUAL",
        active: code !== "10064"
      });
    });
  });
  if (!employees.length) throw new Error("No valid IIL Profile sheet was detected. Salary and Segment must contain values.");
  return employees;
}

function monthKeyFromDate(value) {
  if (!value) return "";
  return value.slice(0, 7);
}

function safeId(value) {
  return text(value).replace(/[^a-zA-Z0-9_-]+/g, "_");
}

export default function IILAttendanceAutomation() {
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState(EMPTY_EMPLOYEE);
  const [sourceRows, setSourceRows] = useState([]);
  const [runRows, setRunRows] = useState([]);
  const [periodEnd, setPeriodEnd] = useState("");
  const [sourceFileName, setSourceFileName] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [runType, setRunType] = useState("SAFDAR");
  const [detectedEmail, setDetectedEmail] = useState(null);

  const mappingByEmpId = useMemo(() => new Map(
    employees.filter((row) => row.clientEmpId).map((row) => [empId(row.clientEmpId), row])
  ), [employees]);
  const unmatched = useMemo(() => runRows.filter((row) => !row.employeeCode), [runRows]);
  const missingFromMaster = useMemo(() => runRows.filter((row) => row.employeeCode && row.masterStatus === "MISSING"), [runRows]);
  const inactiveInAttendance = useMemo(() => runRows.filter((row) => row.masterStatus === "INACTIVE"), [runRows]);
  const wrongRunSource = useMemo(() => runRows.filter((row) => row.masterStatus === "ACTIVE" && (
    (runType === "SAFDAR" && row.masterSource !== "CLIENT_SHEET")
    || (runType === "MANUAL" && row.masterSource !== "MANUAL")
  )), [runRows, runType]);
  const duplicates = useMemo(() => {
    const counts = new Map();
    runRows.forEach((row) => row.employeeCode && counts.set(row.employeeCode, (counts.get(row.employeeCode) || 0) + 1));
    return runRows.filter((row) => row.employeeCode && counts.get(row.employeeCode) > 1);
  }, [runRows]);
  const ready = runRows.length > 0
    && unmatched.length === 0
    && missingFromMaster.length === 0
    && inactiveInAttendance.length === 0
    && wrongRunSource.length === 0
    && duplicates.length === 0
    && runRows.every((row) => number(row.daysWorked, -1) >= 0);
  const filteredEmployees = useMemo(() => {
    const query = header(employeeSearch);
    if (!query) return [];
    return employees.filter((row) => [row.employeeCode, row.clientEmpId, row.name, row.segment]
      .some((value) => header(value).includes(query)));
  }, [employees, employeeSearch]);
  const segmentOptions = useMemo(() => [...new Set([
    ...IIL_SEGMENTS,
    ...employees.map((employee) => text(employee.segment)).filter(Boolean),
    text(form.segment)
  ].filter(Boolean))], [employees, form.segment]);

  const loadEmployees = async () => {
    const snapshot = await getDocs(collection(db, MAPPING_COLLECTION));
    setEmployees(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })).sort((a, b) => text(a.employeeCode).localeCompare(text(b.employeeCode), undefined, { numeric: true })));
  };

  const loadDetectedEmail = async () => {
    const [snapshot, workspaceSnapshot] = await Promise.all([
      getDocs(collection(db, "iil_email_intake")),
      getDocs(collection(db, "iil_monthly_invoice_workspaces"))
    ]);
    const closedMonths = new Set(workspaceSnapshot.docs
      .filter((item) => item.data().status === "PAYROLL_COMPLETED")
      .map((item) => item.id));
    const detected = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))
      .filter((item) => !closedMonths.has(item.monthKey || item.id))
      .sort((a, b) => (b.detectedAt?.seconds || 0) - (a.detectedAt?.seconds || 0))[0] || null;
    setDetectedEmail(detected);
  };

  useEffect(() => {
    loadEmployees().catch((error) => setMessage(`Could not load IIL employee mappings: ${error.message}`));
    loadDetectedEmail().catch((error) => setMessage(`Could not load detected IIL email: ${error.message}`));
  }, []);

  const useDetectedEmail = () => {
    if (!detectedEmail?.rows?.length) return;
    setRunType("SAFDAR");
    setSourceRows(detectedEmail.rows);
    setSourceFileName(detectedEmail.attachmentName || "Detected Safdar email attachment");
    setPeriodEnd(detectedEmail.periodEnd || "");
    setRunRows([]);
    setMessage(`${detectedEmail.rowCount || detectedEmail.rows.length} Safdar row(s) loaded from the latest detected email${detectedEmail.pendingRevision ? ". This is a revision received after approval and requires review" : ""}.`);
  };

  const saveEmployee = async (record, quiet = false) => {
    const normalized = {
      clientEmpId: empId(record.clientEmpId),
      employeeCode: employeeCode(record.employeeCode),
      name: text(record.name),
      source: record.source === "MANUAL" ? "MANUAL" : "CLIENT_SHEET",
      active: record.active !== false,
      salary: number(record.salary, 0),
      segment: text(record.segment),
      cardNo: text(record.cardNo),
      fatherName: text(record.fatherName),
      cnic: text(record.cnic),
      department: text(record.department),
      designation: text(record.designation),
      doj: text(record.doj),
      bank: text(record.bank),
      account: text(record.account),
      pmtMode: text(record.pmtMode),
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.uid || null
    };
    if (!normalized.employeeCode || !normalized.name) throw new Error("Employee Code and Employee Name are required.");
    if (normalized.active && (!normalized.salary || !normalized.segment)) throw new Error("Salary and Segment are required for an active employee.");
    if (normalized.source === "CLIENT_SHEET" && !normalized.clientEmpId) throw new Error("Safdar Emp ID is required for client-sheet employees.");
    const targetId = safeId(normalized.employeeCode);
    const originalId = record._documentId || targetId;
    const targetCollision = employees.find((employee) => employee.id === targetId && employee.id !== originalId);
    if (targetCollision && (empId(targetCollision.clientEmpId) !== normalized.clientEmpId || header(targetCollision.name) !== header(normalized.name))) {
      throw new Error(`Employee Code ${normalized.employeeCode} already belongs to ${targetCollision.name}.`);
    }
    const sameEmpId = normalized.clientEmpId
      ? employees.filter((employee) => employee.id !== originalId && empId(employee.clientEmpId) === normalized.clientEmpId)
      : [];
    const conflictingEmpId = sameEmpId.find((employee) => header(employee.name) !== header(normalized.name) && employee.id !== targetId);
    if (conflictingEmpId) {
      throw new Error(`Safdar Emp ID ${normalized.clientEmpId} already belongs to ${conflictingEmpId.employeeCode} - ${conflictingEmpId.name}. Edit that record instead.`);
    }
    const batch = writeBatch(db);
    batch.set(doc(db, MAPPING_COLLECTION, targetId), normalized, { merge: true });
    const supersededIds = new Set();
    if (originalId !== targetId) supersededIds.add(originalId);
    if (record._documentId) sameEmpId.forEach((employee) => employee.id !== targetId && supersededIds.add(employee.id));
    supersededIds.forEach((id) => batch.delete(doc(db, MAPPING_COLLECTION, id)));
    await batch.commit();
    if (!quiet) setMessage(`${normalized.name} saved to the IIL Employee Master.`);
  };

  const setEmployeeActive = async (record, active) => {
    try {
      setLoading(true);
      await setDoc(doc(db, MAPPING_COLLECTION, safeId(record.employeeCode)), {
        active,
        inactiveAt: active ? null : serverTimestamp(),
        inactiveBy: active ? null : (auth.currentUser?.email || auth.currentUser?.uid || null),
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || null
      }, { merge: true });
      await loadEmployees();
      setMessage(active
        ? `${record.employeeCode} - ${record.name} reactivated and will be expected in attendance.`
        : `${record.employeeCode} - ${record.name} marked inactive and will no longer be expected in attendance.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const editEmployee = (record) => {
    setForm({
      clientEmpId: record.clientEmpId || "",
      employeeCode: record.employeeCode || "",
      name: record.name || "",
      salary: record.salary || "",
      segment: record.segment || "",
      source: record.source || "MANUAL",
      active: record.active !== false,
      cardNo: record.cardNo || "",
      fatherName: record.fatherName || "",
      cnic: record.cnic || "",
      department: record.department || "",
      designation: record.designation || "",
      doj: record.doj || "",
      bank: record.bank || "",
      account: record.account || "",
      pmtMode: record.pmtMode || "",
      _documentId: record.id
    });
    setMessage(`Editing ${record.employeeCode} - ${record.name}. Save to update the master.`);
  };

  const importProfileWorkbook = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setLoading(true);
      const parsed = parseProfileWorkbook(await file.arrayBuffer());
      const existingByCode = new Map(employees.map((employee) => [employee.employeeCode, employee]));
      await Promise.all(parsed.map((row) => {
        const existing = existingByCode.get(row.employeeCode);
        return saveEmployee(existing ? {
          ...row,
          source: existing.source || "MANUAL",
          clientEmpId: existing.clientEmpId || "",
          active: existing.active !== false
        } : row, true);
      }));
      await loadEmployees();
      setMessage(`${parsed.length} profile employee(s) imported. Employee 10064 was marked inactive because he left.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      event.target.value = "";
      setLoading(false);
    }
  };

  const submitEmployee = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      await saveEmployee(form);
      setForm(EMPTY_EMPLOYEE);
      await loadEmployees();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const importMappingWorkbook = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setLoading(true);
      const parsed = parseWorkbook(await file.arrayBuffer());
      const byCode = new Map(employees.map((employee) => [employee.employeeCode, employee]));
      const matched = [];
      const notMatched = [];
      parsed.rows.filter((row) => row.clientEmpId && row.employeeCode).forEach((row) => {
        const existing = byCode.get(row.employeeCode);
        if (!existing) {
          notMatched.push(row);
          return;
        }
        matched.push({ existing, row });
      });
      if (!matched.length) throw new Error("No Safdar Employee Codes matched the Employee Master. The authoritative file must have Emp ID in column A and ALM Employee Code in column B.");
      const safdarByCode = new Map(matched.map(({ row }) => [row.employeeCode, row]));
      await Promise.all(employees.map((existing) => {
        const safdarRow = safdarByCode.get(existing.employeeCode);
        return saveEmployee({
          ...existing,
          clientEmpId: safdarRow?.clientEmpId || "",
          source: safdarRow ? "CLIENT_SHEET" : "MANUAL"
        }, true);
      }));
      await loadEmployees();
      setMessage(`${matched.length} employees permanently marked Provided By Safdar. All other master employees are now Provided By Manual.${notMatched.length ? ` ${notMatched.length} Safdar employee(s) were not found in the master and must be added.` : ""} You do not need to repeat this setup each month.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      event.target.value = "";
      setLoading(false);
    }
  };

  const importAttendance = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const parsed = parseWorkbook(await file.arrayBuffer());
      setSourceRows(parsed.rows);
      setSourceFileName(file.name);
      setMessage(`${parsed.rows.length} ${runType === "SAFDAR" ? "Safdar" : "manual"} attendance row(s) detected from ${parsed.sheetName}. Build this separate monthly run.`);
    } catch (error) {
      setSourceRows([]);
      setSourceFileName("");
      setMessage(error.message);
    }
  };

  const buildRun = () => {
    if (!sourceRows.length) return setMessage(`Import the ${runType === "SAFDAR" ? "Safdar" : "manual"} attendance workbook first.`);
    if (!periodEnd) return setMessage("Select the attendance period end date.");
    const employeeByCode = new Map(employees.map((employee) => [employee.employeeCode, employee]));
    const preparedRows = sourceRows.map((row) => {
      const mapping = mappingByEmpId.get(empId(row.clientEmpId));
      const resolvedCode = row.employeeCode || mapping?.employeeCode || "";
      const master = employeeByCode.get(resolvedCode);
      return {
        ...row,
        employeeCode: resolvedCode,
        name: mapping?.name || master?.name || row.name,
        source: runType === "SAFDAR" ? "CLIENT_SHEET" : "MANUAL",
        masterSource: master?.source || "",
        masterStatus: !resolvedCode || !master ? "MISSING" : master.active === false ? "INACTIVE" : "ACTIVE"
      };
    });
    setRunRows(preparedRows);
    const matchedCount = preparedRows.filter((row) => row.masterStatus === "ACTIVE").length;
    const missingCount = preparedRows.filter((row) => row.masterStatus === "MISSING").length;
    const inactiveCount = preparedRows.filter((row) => row.masterStatus === "INACTIVE").length;
    const wrongSourceCount = preparedRows.filter((row) => row.masterStatus === "ACTIVE" && (
      (runType === "SAFDAR" && row.masterSource !== "CLIENT_SHEET")
      || (runType === "MANUAL" && row.masterSource !== "MANUAL")
    )).length;
    setMessage(`${sourceRows.length} attendance employee(s) received: ${matchedCount} matched, ${missingCount} missing, ${inactiveCount} inactive, ${wrongSourceCount} in the wrong run type. ${missingCount || inactiveCount || wrongSourceCount ? "Processing is stopped until every exception is resolved." : "All source rows are accounted for; review before approval."}`);
  };

  const updateDays = (index, value) => {
    setRunRows((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, daysWorked: value } : row));
  };

  const updateOvertime = (index, value) => {
    setRunRows((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, otHours: value } : row));
  };

  const saveRun = async () => {
    if (!ready) {
      setMessage("Resolve missing/inactive master employees, duplicate codes, and missing days before approval.");
      return false;
    }
    const monthKey = monthKeyFromDate(periodEnd);
    const runId = `${monthKey}_${runType}`;
    const employeeCodes = [...new Set(runRows.map((row) => employeeCode(row.employeeCode)).filter(Boolean))];
    try {
      await runTransaction(db, async (transaction) => {
        const lockRefs = employeeCodes.map((code) => doc(db, "iil_monthly_employee_locks", `${monthKey}_${safeId(code)}`));
        const runRef = doc(db, RUN_COLLECTION, runId);
        const workspaceRef = doc(db, "iil_monthly_invoice_workspaces", monthKey);
        const [lockSnapshots, existingRunSnapshot, workspaceSnapshot] = await Promise.all([
          Promise.all(lockRefs.map((ref) => transaction.get(ref))),
          transaction.get(runRef),
          transaction.get(workspaceRef)
        ]);
        if (workspaceSnapshot.exists() && workspaceSnapshot.data().status === "PAYROLL_COMPLETED") {
          throw new Error(`Payroll for ${monthKey} is already completed. Reopen the monthly payroll before adding attendance.`);
        }
        const conflicts = lockSnapshots
          .filter((snapshot) => snapshot.exists() && snapshot.data().runId !== runId)
          .map((snapshot) => snapshot.data().employeeCode);
        if (conflicts.length) {
          throw new Error(`Monthly lock conflict: ${conflicts.join(", ")} already used in another IIL invoice/payroll run for ${monthKey}.`);
        }
        const existingRun = existingRunSnapshot.exists() ? existingRunSnapshot.data() : null;
        const existingSafdarIsVerified = (existingRun?.rows || []).length > 0
          && existingRun.rows.every((row) => row.masterSource === "CLIENT_SHEET");
        if (runType === "SAFDAR" && existingRun?.status === "APPROVED_FOR_INVOICE" && existingSafdarIsVerified) {
          const previousCodes = [...(existingRun.employeeCodes || [])].map(employeeCode).sort();
          const incomingCodes = [...employeeCodes].sort();
          if (JSON.stringify(previousCodes) !== JSON.stringify(incomingCodes)) {
            throw new Error(`The approved Safdar run for ${monthKey} is protected and cannot be replaced by a different employee list. Use Manual run for PPRC and remaining employees.`);
          }
        }
        const existingRows = runType === "MANUAL" ? (existingRun?.rows || []) : [];
        const existingCodes = new Set(existingRows.map((row) => employeeCode(row.employeeCode)));
        const repeatedCodes = employeeCodes.filter((code) => existingCodes.has(code));
        if (repeatedCodes.length) {
          throw new Error(`Manual batch not added: Employee Code(s) ${repeatedCodes.join(", ")} were already saved in an earlier manual batch for ${monthKey}.`);
        }
        const savedRows = runRows.map(({ marks, sheetName, ...row }) => ({
          ...row,
          batchFileName: sourceFileName,
          daysWorked: number(row.daysWorked),
          otHours: number(row.otHours)
        }));
        const combinedRows = runType === "MANUAL" ? [...existingRows, ...savedRows] : savedRows;
        const combinedCodes = [...new Set(combinedRows.map((row) => employeeCode(row.employeeCode)).filter(Boolean))];
        const sourceFiles = [...new Set([...(existingRun?.sourceFiles || []), sourceFileName].filter(Boolean))];
        lockRefs.forEach((ref, index) => transaction.set(ref, {
          monthKey,
          employeeCode: employeeCodes[index],
          runId,
          runType,
          sourceFileName,
          lockedAt: serverTimestamp(),
          lockedBy: auth.currentUser?.email || auth.currentUser?.uid || null
        }));
        transaction.set(runRef, {
          runId,
          runType,
          monthKey,
          periodEnd,
          sourceFileName: sourceFiles.join(" + "),
          sourceFiles,
          status: "APPROVED_FOR_INVOICE",
          rowCount: combinedRows.length,
          employeeCodes: combinedCodes,
          rows: combinedRows,
          approvedAt: serverTimestamp(),
          approvedBy: auth.currentUser?.email || auth.currentUser?.uid || null
        }, { merge: true });
      });
      setMessage(`${runType === "SAFDAR" ? "Safdar" : "Manual batch"} approved. ${employeeCodes.length} employee code(s) added and remembered for ${monthKey}.`);
      return true;
    } catch (error) {
      setMessage(error.message || "Could not lock the monthly employee codes.");
      return false;
    }
  };

  const openInInvoiceGenerator = async () => {
    if (!ready) return setMessage("Resolve all exceptions before opening the invoice generator.");
    const saved = await saveRun();
    if (!saved) return;
    const activeEmployees = employees.filter((row) => row.active !== false);
    sessionStorage.setItem("iilApprovedAttendanceRun", JSON.stringify({
      periodEnd,
      runType,
      sourceFileName,
      employees: activeEmployees,
      rows: runRows.map((row) => ({ ...row, daysWorked: number(row.daysWorked), otHours: number(row.otHours) }))
    }));
    window.history.pushState({}, "", "/iil-invoice");
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  const downloadPreparedWorkbook = () => {
    if (!ready) return setMessage("The run must have no unmatched IDs, duplicate codes, or missing days.");
    const periodDays = 31;
    const dayHeaders = Array.from({ length: periodDays }, (_, index) => index + 1);
    const rows = [
      ["Employee Code", "Emp ID", "Employee Name", ...dayHeaders, "P", "A", "S", "G", "Salary Days", "Attendance Source"],
      ...runRows.map((row) => {
        const days = number(row.daysWorked);
        const marks = row.marks?.length ? row.marks.slice(0, periodDays) : Array.from({ length: periodDays }, (_, index) => index < days ? "P" : "A");
        while (marks.length < periodDays) marks.push("");
        return [row.employeeCode, row.clientEmpId, row.name, ...marks, row.p || days, row.a || Math.max(periodDays - days, 0), row.s || 0, row.g || 0, days, row.source];
      })
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    worksheet["!cols"] = [{ wch: 16 }, { wch: 12 }, { wch: 28 }, ...dayHeaders.map(() => ({ wch: 4 })), { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 12 }, { wch: 18 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");
    const output = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([output]), `IIL_Attendance_Prepared_${monthKeyFromDate(periodEnd)}.xlsx`);
  };

  return (
    <div className="app-shell">
      <Sidebar />
      <main className="main-content">
        <header className="page-header">
          <div>
            <div className="eyebrow">IIL Automation</div>
            <h1 className="page-title">Attendance Intake and Mapping</h1>
            <p className="page-copy">Maps Safdar's attendance-machine Emp ID to ALM Employee Code, adds manually maintained employees, and creates a reviewed workbook for the existing IIL invoice engine.</p>
          </div>
          <span className={`status-pill ${detectedEmail ? "success" : "warning"}`}>{detectedEmail ? `Latest email: ${detectedEmail.monthKey}` : "Waiting for Safdar email"}</span>
        </header>
        <IILSubnav />

        <section className="card-grid">
          <div className="metric-card">
            <div className="metric-label">Active Employee Mappings</div>
            <div className="metric-value">{employees.filter((employee) => employee.active !== false).length}</div>
            <div className="page-copy" style={{ marginTop: 8 }}>
              {employees.length} total · {employees.filter((employee) => employee.active === false).length} inactive
            </div>
          </div>
          <div className="metric-card"><div className="metric-label">{runType === "SAFDAR" ? "Safdar" : "Manual"} Upload Rows</div><div className="metric-value">{sourceRows.length}</div></div>
          <div className="metric-card"><div className="metric-label">Run Exceptions</div><div className="metric-value">{unmatched.length + missingFromMaster.length + inactiveInAttendance.length + wrongRunSource.length + duplicates.length}</div></div>
        </section>

        {message && <div className="panel" style={{ padding: 16, marginBottom: 20 }}>{message}</div>}

        <section className="panel">
          <div className="panel-header"><div><h2 className="panel-title">1. IIL Employee Master</h2><p className="page-copy">Import a previously edited sheet once, or add employees individually. Emp IDs are preserved with leading zeroes.</p></div></div>
          <div className="panel-body">
            <div className="form-grid">
              <label className="file-drop"><div className="file-title">Import IIL Profile Master</div><div className="file-copy">Imports salary, segment, bank and payroll details for all employees.</div><input className="hidden-input" type="file" accept=".xlsx,.xls" onChange={importProfileWorkbook} /></label>
              <div className="file-drop" style={{ cursor: "default" }}><div className="file-title">Mailbox Rule</div><div className="file-copy">From safdar.ali@iil.com.pk · subject contains Attendance · expected 19th–22nd. Imported files still require review.</div></div>
            </div>
            <form className="form-grid" onSubmit={submitEmployee} style={{ marginTop: 18 }}>
              <div className="field"><label>Employee Code</label><input className="input" value={form.employeeCode} onChange={(e) => setForm({ ...form, employeeCode: e.target.value })} /></div>
              <div className="field"><label>Safdar Emp ID</label><input className="input" value={form.clientEmpId} onChange={(e) => setForm({ ...form, clientEmpId: e.target.value })} placeholder="Required for client-sheet employees" /></div>
              <div className="field"><label>Employee Name</label><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="field"><label>Provided By</label><select className="input" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}><option value="CLIENT_SHEET">Safdar</option><option value="MANUAL">Manual entry</option></select></div>
              <div className="field"><label>Monthly Salary</label><input className="input" type="number" min="0" value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} /></div>
              <div className="field"><label>Invoice Segment</label><select className="input" value={form.segment} onChange={(e) => setForm({ ...form, segment: e.target.value })}><option value="">Select invoice segment</option>{segmentOptions.map((segment) => <option key={segment} value={segment}>{segment}</option>)}</select></div>
              <div className="field"><label>Bank</label><input className="input" value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} /></div>
              <div className="field"><label>Account Number</label><input className="input" value={form.account} onChange={(e) => setForm({ ...form, account: e.target.value })} /></div>
              <div className="button-row field-wide"><button className="btn btn-primary" disabled={loading}>Save Employee</button></div>
            </form>
            {employees.length > 0 && <><div className="field" style={{ marginTop: 20 }}><label>Search Employee Master</label><input className="input" value={employeeSearch} onChange={(e) => setEmployeeSearch(e.target.value)} placeholder="Search employee code, Emp ID, name or segment" /></div>{employeeSearch.trim() ? <div className="table-wrap" style={{ paddingTop: 20 }}><table className="data-table"><thead><tr><th>Employee Code</th><th>Safdar Emp ID</th><th>Name</th><th>Segment</th><th>Provided By</th><th>Status</th><th>Actions</th></tr></thead><tbody>{filteredEmployees.length ? filteredEmployees.map((row) => <tr key={row.id}><td>{row.employeeCode}</td><td>{row.clientEmpId || "—"}</td><td>{row.name}</td><td>{row.segment || "Missing"}</td><td>{row.source === "MANUAL" ? "Manual entry" : "Safdar"}</td><td><span className={`status-pill ${row.active === false ? "danger" : "success"}`}>{row.active === false ? "Inactive" : "Active"}</span></td><td><div className="table-actions"><button className="btn btn-muted" type="button" onClick={() => editEmployee(row)}>Edit</button><button className={`btn ${row.active === false ? "btn-success" : "btn-danger"}`} type="button" disabled={loading} onClick={() => setEmployeeActive(row, row.active === false)}>{row.active === false ? "Reactivate" : "Mark Inactive"}</button></div></td></tr>) : <tr><td colSpan="7">No employee record matches this search.</td></tr>}</tbody></table></div> : <p className="page-copy" style={{ marginTop: 12 }}>Employee records are hidden. Search for an employee when you need to edit or deactivate them.</p>}</>}
          </div>
        </section>

        <section className="panel" style={{ marginTop: 22 }}>
          <div className="panel-header"><div><h2 className="panel-title">2. Build Monthly Attendance Run</h2><p className="page-copy">Until the mailbox is connected, upload the original attachment here. The same parser will be used by automatic email intake.</p></div><span className={`status-pill ${ready ? "success" : "warning"}`}>{ready ? "Ready" : "Review required"}</span></div>
          <div className="panel-body">
            <div className="form-grid">
              <div className="field"><label>Separate Run Type</label><select className="input" value={runType} onChange={(e) => { setRunType(e.target.value); setSourceRows([]); setRunRows([]); setSourceFileName(""); setMessage(""); }}><option value="SAFDAR">Safdar attendance and overtime</option><option value="MANUAL">Manual attendance upload</option></select><p className="page-copy">Employee codes are locked across both types after approval, so the same code cannot enter invoice/payroll twice in one month.</p></div>
              <label className="file-drop"><div className="file-title">{sourceFileName || `Choose ${runType === "SAFDAR" ? "Safdar" : "Manual"} Attendance Sheet`}</div><div className="file-copy">Auto-detects Employee Code/Emp ID, employee name, Salary Days and overtime.</div><input className="hidden-input" type="file" accept=".xlsx,.xls" onChange={importAttendance} /></label>
              <div className="field"><label>Period End Date</label><input className="input" type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} /><button className="btn btn-primary" type="button" onClick={buildRun} style={{ marginTop: 12 }}>Build {runType === "SAFDAR" ? "Safdar" : "Manual"} Run</button></div>
            </div>
            {detectedEmail && <div className="panel" style={{ padding: 16, marginTop: 18 }}><strong>Detected Safdar email available</strong><p className="page-copy">{detectedEmail.attachmentName} · {detectedEmail.rowCount} employees · period {detectedEmail.monthKey}{detectedEmail.pendingRevision ? " · revision received after approval" : ""}</p><button className="btn btn-primary" type="button" onClick={useDetectedEmail}>Load Detected Safdar Run</button></div>}
            {(unmatched.length > 0 || missingFromMaster.length > 0 || inactiveInAttendance.length > 0) && <div className="panel" style={{ padding: 16, marginTop: 18, borderColor: "#f04438" }}><strong>Attendance validation stopped.</strong><p className="page-copy">{unmatched.length + missingFromMaster.length} employee(s) are missing from the Master and {inactiveInAttendance.length} employee(s) are inactive. Update the Master or correct the uploaded sheet, then rebuild.</p></div>}
            {runRows.length > 0 && <div className="table-wrap" style={{ paddingTop: 20 }}><table className="data-table"><thead><tr><th>Employee Code</th><th>Emp ID</th><th>Name</th><th>Run Type</th><th>Salary Days</th><th>OT Hours</th><th>Status</th></tr></thead><tbody>{runRows.map((row, index) => { const duplicate = row.employeeCode && duplicates.some((item) => item.employeeCode === row.employeeCode); const wrongSource = row.masterStatus === "ACTIVE" && ((runType === "SAFDAR" && row.masterSource !== "CLIENT_SHEET") || (runType === "MANUAL" && row.masterSource !== "MANUAL")); return <tr key={`${row.clientEmpId}-${row.employeeCode}-${index}`}><td>{row.employeeCode || <span style={{ color: "var(--danger)" }}>Unmatched</span>}</td><td>{row.clientEmpId || "—"}</td><td>{row.name}</td><td>{runType === "SAFDAR" ? "Safdar" : "Manual"}</td><td><input className="input" style={{ width: 100, padding: 8 }} type="number" min="0" max="31" step="0.5" value={row.daysWorked} readOnly={runType === "SAFDAR"} onChange={(e) => updateDays(index, e.target.value)} /></td><td><input className="input" style={{ width: 100, padding: 8 }} type="number" min="0" step="0.25" value={row.otHours || 0} readOnly={runType === "SAFDAR"} onChange={(e) => updateOvertime(index, e.target.value)} /></td><td>{row.masterStatus === "MISSING" ? <span className="status-pill danger">Update Master</span> : row.masterStatus === "INACTIVE" ? <span className="status-pill danger">Inactive Employee</span> : wrongSource ? <span className="status-pill danger">Wrong Run Type</span> : duplicate ? <span className="status-pill danger">Duplicate</span> : <span className="status-pill success">Matched</span>}</td></tr>; })}</tbody></table></div>}
            <div className="button-row"><button className="btn btn-muted" type="button" disabled={!ready} onClick={saveRun}>Approve and Save Run</button><button className="btn btn-muted" type="button" disabled={!ready} onClick={downloadPreparedWorkbook}>Download Prepared Workbook</button><button className="btn btn-success" type="button" disabled={!ready} onClick={openInInvoiceGenerator}>Open in IIL Invoice</button></div>
          </div>
        </section>
      </main>
    </div>
  );
}
